/**
 * How often the OS actually wakes the stay-visible background task, and what
 * happened when it did. Imports nothing so tests can load it
 * (stay-visible.ts pulls expo-location / react-native).
 *
 * The question: `timeInterval: 60_000` is Android-only, so Android pings
 * every minute against a 300 s presence expiry; iOS has no such clock and the
 * roadmap concluded its delivery is movement-driven — a stationary phone
 * going invisible. That conclusion is NOT established: expo-location already
 * calls startMonitoringSignificantLocationChanges unconditionally, deferred
 * reporting defaults to 0, background modes and flags are already maximal,
 * and startUpdatingLocation is a CONTINUOUS service. The one unknown is a
 * number nobody has — how often a still iPhone is actually woken. This
 * record exists to produce it.
 *
 * Separate from `StayVisiblePing` on purpose: that record is load-bearing
 * (three failures trips handleBreak — session stopped, user HIDDEN). This is
 * a record NOTHING acts on, read by one debug row and tests — a probe can
 * never be the failure (timing-probe.ts).
 *
 * TEMPORARY: when the wake cadence of a still iPhone is known, this comes
 * out (timing-probe.ts: remove call sites when their question is answered).
 */

/** Every way one wake can end. There is no silent exit; that was the bug. */
export type WakeOutcome =
  | "ok"
  | "ping_failed"
  | "error"
  | "no_fix"
  | "no_accuracy"
  | "signed_out";

export type StayVisibleDiagnostics = {
  /** When counting started — the window these totals are measured over. */
  since: string | null;
  /** Total wakes. The number the whole exercise is for. */
  wakes: number;
  lastWakeAt: string | null;
  /** Wakes where both calls landed. */
  ok: number;
  lastOkAt: string | null;
  /** Wakes that reached the server and were refused or failed. */
  pingFailed: number;
  /** The three exits that used to record NOTHING, so a woken-but-useless task
   *  was indistinguishable from a task the OS never called. */
  error: number;
  noFix: number;
  signedOut: number;
  /** Already counted as a ping failure; kept apart because its cause differs. */
  noAccuracy: number;
};

export const EMPTY_DIAGNOSTICS: StayVisibleDiagnostics = {
  since: null,
  wakes: 0,
  lastWakeAt: null,
  ok: 0,
  lastOkAt: null,
  pingFailed: 0,
  error: 0,
  noFix: 0,
  signedOut: 0,
  noAccuracy: 0,
};

/**
 * A ceiling on every counter, because "no request, query, render, job, retry or
 * payload may grow without a defined limit" applies to a counter that a
 * multi-day background session increments too. Saturating rather than wrapping:
 * a pinned maximum reads as "very many", where a wrapped one reads as "few" and
 * would invert the conclusion.
 */
export const MAX_COUNT = 99_999;

const bump = (n: number): number => (n >= MAX_COUNT ? MAX_COUNT : n + 1);

/**
 * Milliseconds since the last SUCCESSFUL publication, or `null` if there has
 * not been one in this window.
 *
 * <p>The soak has to answer four things, and this is the only one the counters
 * could not: did presence ever go stale? Totals say how many wakes landed, not
 * whether two of them were far enough apart for the server to expire the row.
 * A `wakes` count that looks healthy is compatible with a gap that crossed the
 * TTL and made the user invisible in between.
 *
 * Derived rather than logged as a wall-clock pair, because the alternative is
 * reconstructing gaps from logcat timestamps — which works only while the
 * capture is unbroken, and a soak long enough to be interesting is exactly the
 * one where it is not.
 *
 * Returns `null`, never a negative number: system clock corrections move wall
 * time backwards, and a negative gap read as "very fresh" would hide the
 * failure this exists to expose. `lastOkAt` is an ISO string from a previous
 * process, so it cannot be compared against a monotonic clock.
 */
export function msSinceLastOk(
  previous: StayVisibleDiagnostics,
  nowIso: string,
): number | null {
  if (!previous.lastOkAt) return null;

  const then = Date.parse(previous.lastOkAt);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(then) || !Number.isFinite(now)) return null;

  const elapsed = now - then;
  return elapsed >= 0 ? elapsed : null;
}

/** Begin a fresh window. Called when the background task is (re)started. */
export function startDiagnostics(atIso: string): StayVisibleDiagnostics {
  return { ...EMPTY_DIAGNOSTICS, since: atIso };
}

/**
 * Record one wake and how it ended.
 *
 * Every exit path calls this exactly once, which is what makes `wakes` equal
 * the number of times the OS actually called us — the one figure that separates
 * "iOS never woke the task" from "iOS woke it and it achieved nothing".
 */
export function countWake(
  previous: StayVisibleDiagnostics,
  outcome: WakeOutcome,
  atIso: string,
): StayVisibleDiagnostics {
  const next: StayVisibleDiagnostics = {
    ...previous,
    wakes: bump(previous.wakes),
    lastWakeAt: atIso,
  };

  switch (outcome) {
    case "ok":
      return { ...next, ok: bump(next.ok), lastOkAt: atIso };
    case "ping_failed":
      return { ...next, pingFailed: bump(next.pingFailed) };
    case "error":
      return { ...next, error: bump(next.error) };
    case "no_fix":
      return { ...next, noFix: bump(next.noFix) };
    case "signed_out":
      return { ...next, signedOut: bump(next.signedOut) };
    case "no_accuracy":
      return { ...next, noAccuracy: bump(next.noAccuracy) };
  }
}

/** Parse a stored record, tolerating anything that is not one. */
export function parseDiagnostics(raw: string | null): StayVisibleDiagnostics {
  if (!raw) return EMPTY_DIAGNOSTICS;
  try {
    const parsed = JSON.parse(raw) as Partial<StayVisibleDiagnostics>;
    if (typeof parsed !== "object" || parsed === null) return EMPTY_DIAGNOSTICS;
    return { ...EMPTY_DIAGNOSTICS, ...parsed };
  } catch {
    return EMPTY_DIAGNOSTICS;
  }
}

/**
 * The line a human reads off the phone.
 *
 * Deliberately one dense string rather than a laid-out panel: this is scaffolding
 * for one question, and it is removed once answered.
 *
 * Minutes rather than a rate, because a rate computed over a two-minute window
 * reads as authoritative and is noise. The reader can divide.
 */
export function summariseDiagnostics(
  diagnostics: StayVisibleDiagnostics,
  nowMs: number,
): string {
  const { since, wakes, ok, lastOkAt, lastWakeAt } = diagnostics;
  if (!since) return "not started";

  const windowMin = Math.max(0, Math.round((nowMs - Date.parse(since)) / 60_000));
  const ageOf = (iso: string | null): string =>
    iso === null ? "never" : `${Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000))}s ago`;

  const skipped =
    diagnostics.error + diagnostics.noFix + diagnostics.signedOut + diagnostics.noAccuracy;

  return [
    `${windowMin}m window`,
    `${wakes} wakes (last ${ageOf(lastWakeAt)})`,
    `${ok} ok (last ${ageOf(lastOkAt)})`,
    `${diagnostics.pingFailed} failed`,
    `${skipped} skipped`,
  ].join(" · ");
}
